import { Box, Container, Grid, Paper, Tab, Tabs, Typography } from "@mui/material";
import ConnectedAccount from "../../../models/ConnectedAccount";
import MultiInputCloudwatchRemediate from "../common/MultiInputCloudwatchRemediate";
import FTRNonDisruptiveRecommendations from "./FTRNonDisruptiveRecommendations";
import { FC, memo, useCallback, useState } from "react";

interface FTRNonDisruptiveCloudWatchProps {
  targetAccountType: string;
  technicalReviewArn: string;
  connectedAccounts: Array<ConnectedAccount>;
  selectedControls: any[]; // Array of controls currently selected in the parent component
  updateSelectedControls: (control: any, isSelected: boolean) => void; // Function to update the selected controls state
  setUnlockSteps: React.Dispatch<
    React.SetStateAction<{
      step2: boolean;
      step3: boolean;
      step4: boolean;
      step5: boolean;
    }>
  >;
	changeTab: (index: number) => void;
}

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

const TabPanel = memo(({ children, value, index, ...other }: TabPanelProps) => {
	return (
		<Container
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && (
				<Box sx={{ p: 3 }}>
					<Typography component="span">{children}</Typography>
				</Box>
			)}
		</Container>
	);
});

/**
 * Used for tabs
 */
function a11yProps(index: number) {
	return {
		id: `simple-tab-${index}`,
		"aria-controls": `simple-tabpanel-${index}`,
	};
}

/**
 * Main Page of Failed Non Disruptive Recommendations of FTR and CIS 1.4 and CloudWatch Remediation
 * @param targetAccountType returns if account is AUTOMATED or READ_ONLY
 * @param technicalReviewArn technical reveiew arn that was selected when creating the framework
 * @param connectedAccounts state of connectedAccounts information
 * @param selectedControls state to keep track of controls that are selected on this page
 * @param updateSelectedControls called when a control is selected to be remediated from this page
 * @param setUnlockSteps which step to unlock and lock in the tabs
 * @param changeTab tab to switch to after this page is completed
 * @returns all Failed Non Disruptive Recommendations of FTR and CIS 1.4 selected
 */
const FTRNonDisruptiveCloudWatchRemediate: FC<FTRNonDisruptiveCloudWatchProps> = ({
	targetAccountType,
  technicalReviewArn,
  connectedAccounts,
  selectedControls,
  updateSelectedControls,
	setUnlockSteps,
	changeTab,
}: FTRNonDisruptiveCloudWatchProps) => {
	const [value, setValue] = useState(0);
	const [disabled, setDisabled] = useState(true);
	// Handles which tab to change to
	const handleChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
		setDisabled(false);
		setValue(newValue);
	}, [setValue]);

	//Handle that unlocks step 3
	const handleUnlockSteps = useCallback(() => {
		setUnlockSteps((prevState) => ({
			...prevState,
			step2: false, // Lock step 2
			step3: true, // Unlock step 3
		}));
	}, []);

	const tabPanels = [
		{
			index: 0,
			component: (
				<FTRNonDisruptiveRecommendations
					targetAccountType={targetAccountType}
					technicalReviewArn={technicalReviewArn}
					connectedAccounts={connectedAccounts}
					selectedControls={selectedControls} // Pass the centralized state
					updateSelectedControls={updateSelectedControls} // Pass the updater function
					setUnlockSteps={setUnlockSteps}
					changeRemediateTab={(
						index: number,
					) => {
						setValue(index);
					}}
					changeTab={changeTab}
				/>
			),
		},
		{
			index: 1,
			component: (
				<MultiInputCloudwatchRemediate
					arn={technicalReviewArn}
					changeTab={()=>{
						handleUnlockSteps();
						changeTab(2);
					}}
				/>
			),
		},
		// Add placeholders for other panels if needed
	];

	return (
		<Grid
			container
			sx={{ p: 2 }}
			spacing={2}
			alignItems="stretch"
			flexDirection={"column"}
			component={Paper}
			maxWidth="lg"
		>
			<Box sx={{ width: "100%" }}>
				<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
					<Tabs
						value={value}
						onChange={handleChange}
						textColor="secondary"
						indicatorColor="secondary"
						variant="fullWidth"
					>
						<Tab label="Non-Disruptive Recommendations" disabled={!disabled} {...a11yProps(0)} />
						<Tab label="Cloudwatch Remediation" disabled={disabled} {...a11yProps(1)} />
					</Tabs>
				</Box>
				{tabPanels.map(({ index, component }) => (
					<TabPanel value={value} index={index} key={`MultiFramework-${index}`}>
						{component}
					</TabPanel>
				))}
			</Box>
		</Grid>
	);
};

export default FTRNonDisruptiveCloudWatchRemediate;